import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { leadSourceEndpoint } from "@/config/endpoints";

/** One source whose sync needs a person to reconnect it. */
export type ReconnectRequiredSource = {
  id: number;
  name: string | null;
  type: string;
  reconnectReason: string | null;
  /** ISO time the current failing streak began. */
  syncFailingSince: string | null;
  lastSynced: string | null;
};

/**
 * Under the "lead-sources" prefix on purpose: every source mutation already
 * invalidates ["lead-sources"], so saving a reconnect refreshes this list too.
 */
export const RECONNECT_REQUIRED_QUERY_KEY = ["lead-sources", "reconnect-required"] as const;

/**
 * Open "Reconnect required" sources (server: GET
 * /core/source/lead-source/reconnect-required — Super Admins only, [] for
 * everyone else). Read on load so an admin who was offline when the push went
 * out still gets the popup at login; live pushes invalidate it.
 */
export function useReconnectRequiredSources(enabled: boolean) {
  const query = useQuery({
    queryKey: RECONNECT_REQUIRED_QUERY_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get(`${leadSourceEndpoint}/reconnect-required`);
      return (res.data?.sources ?? []) as ReconnectRequiredSource[];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    // Backstop for a missed socket push (reconnects, sleeping laptop).
    refetchInterval: enabled ? 15 * 60 * 1000 : false,
  });

  return { sources: query.data ?? [], isLoading: query.isLoading };
}
